import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Badge } from '@ge/components/badge';
import { PageContainer } from '@ge/components/containers';
import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { Select } from '@ge/components/select';
import { DraggableTable } from '@ge/components/table';
import { TooltipCell } from '@ge/components/table/table';
import { useManageColumnVisibility } from '@ge/components/table/use-manage-column-visibility';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import PageHeader from '@ge/feat-monitor/components/page-header';
import { useFeaturePrefs } from '@ge/hooks/feature-prefs';
import { useLocalStorage } from '@ge/hooks/use-local-storage';
import {
  Capability,
  AdminDefs,
  FLAGGED_STATES,
  EventManagement,
  DistributionLists,
  SeverityType,
  EventLables,
  ViewBy,
  TableFilterTypes,
} from '@ge/models/constants';
import { AuthRender } from '@ge/shared/components/auth-render';
import { ColumnSelectMenu } from '@ge/shared/components/column-select-menu';
import { useAllEventCode, useEM } from '@ge/shared/data-hooks/use-event-management';
import { useAllCustomCode } from '@ge/shared/data-hooks/use-event-management';
import { useEventMaps } from '@ge/shared/data-hooks/use-handling-procedure';
import { AppScopes } from '@ge/shared/models/scopes';
import { debounce } from '@ge/shared/util';
import { globalColors } from '@ge/tokens';
import SubNavigation from '@ge/web-client/src/app/components/navigation/sub-navigation';

import {
  EventManagementColumnsDefs,
  EventManagementColumns,
} from '../../models/event-management-table-cols';
import { ReactComponent as CheckMark } from '../event-management/assets/tick.svg';
import { PopupMenu } from '../menu-pop-up';

//@custom styles
const AddEMWrapper = styled.div`
  cursor: pointer;
  margin: 0.5em 1em 0 0;
  width: 85px;
  display: flex;
`;

const FlaggedStateIcon = styled.div`
  min-width: 16px;
  min-height: 14px;
  padding: 2px 3px;
  margin-right: 4px;
  border-radius: 4px;
  text-align: center;
  display: inline-block;
`;

const StyledDraggableTable = styled(DraggableTable)`
  th:not(:last-child),
  td:not(:last-child) {
    border-right: solid 1px ${(props) => props.theme.subNavigation.linkBorderColor} !important;
  }
  td{
    &.group-last {
      top: 0px !important;
      z-index: 8 !important;
   }
   span {
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
  }
`;

const SelectWrapper = styled.div`
  text-align: left;
  left: 2em;
  z-index: 1;
  margin: 0px 10px;
  transform: translateY(-21px);
  label {
    color: ${({ theme }) => theme.sensorReadingsChart.labelColor};
  }
  &:nth-child(even) {
    margin: 17px 0px 0px;
  }
`;

const HeaderWrap = styled.div`
  margin: 0 0 2.3em 0;
`;

const EllipsisDivWrapper = styled.div`
  max-width: 60%;
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
`;

const CellWrapBadge = styled(Badge)`
  float: right;
`;

export const DLMetric = {
  NAME: 'name',
};

const SelectContainer = styled.div`
  display: flex;
  justify-container: space-between;
  align-items: center;
  width: 100%;
`;

const SeverityIcon = styled.svg`
  display: inline-block;
  padding-top: 2px;
  vertical-align: middle;
  height: 22px;
  width: 16px;
  & path:nth-child(1) {
    fill: ${(props) => props.theme.eventSeverity.safetyCritical};
  }
  & path:nth-child(2),
  path:nth-child(3) {
    fill: ${(props) => props.theme.eventSeverity.safetyCriticalExclamatory};
  }
`;

const AlarmIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.FAULT_ALARM,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.falutAlarm};
  }
`;

const WarningIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.WARNING_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.warning};
  }
`;

const InformationIcon = styled(Icon).attrs(() => ({
  size: 18,
  icon: Icons.INFORMATIONAL_SEVERITY,
  viewbox: '0 0 16 16',
}))`
  padding-top: 2px;
  path {
    fill: none;
    stroke: ${(props) => props.theme.eventSeverity.informational};
  }
`;

//@functional component
const EventManagementTable = ({
  columns,
  onRowSelect,
  setFilterDefs,
  filterDefs,
  openEMSidePanel,
  sortAction,
  sortMetric,
  viewDetail,
  sortDirection,
  setVisibleColumns,
  updateColumnVisibility,
  resetFilter,
  selectedRow,
}) => {
  const { search } = useLocation();
  const searchWorker = useRef(null);
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const [options, setOptions] = useState([]);
  const [currentSearch, setCurrentSearch] = useState({});
  const { t, ready } = useTranslation(['monitor.sites']);

  const [visibleEventCodes, setVisibleEventCodes] = useState([]);
  const [visibleCustomCode, setVisibleCustomCode] = useState([]);
  const [pageSize] = useState(100);
  const injectedSearchesRef = useRef(new Set());

  const [primarayOptions] = useState(EventLables);
  const [secondaryOptions, setSecondaryOptions] = useState();
  const [eventCode, setEventCode] = useState(false);
  const [customCode, setCustomCode] = useState(false);
  const [viewBy, setViewBy] = useLocalStorage(
    `${AdminDefs.EVENT_MANAGMENT_LIST_VIEW}.${'view'}`,
    primarayOptions[0],
  );

  const [selectedMapId, setSelectedMapId] = useLocalStorage(
    `${AdminDefs.EVENT_MANAGMENT_LIST_SORT_STATE_ID}.${'mapId'}`,
    '',
  );

  const [selectedCustomEventId, setSelectedCustomEventId] = useLocalStorage(
    `${AdminDefs.EVENT_MANAGMENT_LIST_SORT_STATE_ID}.${'customEventId'}`,
    null,
  );

  const previousViewByValueRef = useRef(viewBy?.value);

  const isViewBy = (state, view) => {
    return state?.value === view;
  };

  useEffect(() => {
    if (viewBy?.value === previousViewByValueRef.current) return;

    if (!columns) return;

    previousViewByValueRef.current = viewBy?.value;

    setSelectedMapId(null);

    const isCustomView = viewBy?.value === ViewBy.Custom;

    const updatedColumns = columns.map((group) => {
      if (group.id === EventManagementColumns.GROUP_EVENTS) {
        return {
          ...group,
          cols: group.cols.map((col) => {
            if (
              col.id === EventManagementColumns.PARENT_EVENTS ||
              col.id === EventManagementColumns.CHILD_EVENTS
            ) {
              return {
                ...col,
                visible: !isCustomView,
              };
            }
            return col;
          }),
        };
      }

      // Handle GROUP_ASSET - show block case escalation only in custom view
      if (group.id === EventManagementColumns.GROUP_ASSET) {
        return {
          ...group,
          cols: group.cols.map((col) => {
            if (col.id === EventManagementColumns.BLOCK_CASE_ESCALATION) {
              return {
                ...col,
                visible: isCustomView,
                filterType: isCustomView ? TableFilterTypes.CHECKBOXES : undefined,
              };
            }
            return col;
          }),
        };
      }

      return group;
    });

    setVisibleColumns(updatedColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy?.value, columns]);

  const apiMapId = viewBy?.value === ViewBy.Custom ? 'custom_cases' : selectedMapId?.value;

  useEffect(() => {
    if (viewBy?.value !== ViewBy.Custom) {
      setSelectedCustomEventId(null);
    }
  }, [viewBy]);

  const { eventManagementLists, filterValues, isFetching, isLoading } = useEM({
    search: currentSearch,
    filters: filterDefs,
    sortDirection,
    sortMetric,
    // mapId: selectedMapId?.value,
    mapId: apiMapId,
    viewBy: viewBy?.value,
    selectedCustomEventId,
  });

  useEffect(() => {
    searchWorker.current = new Worker(
      new URL('../event-management/event-search-worker', import.meta.url),
    );
    return () => {
      if (searchWorker.current) {
        searchWorker.current.terminate();
      }
    };
  }, []);

  const { eventCodeList } = useAllEventCode(eventCode);
  const { customCodeList } = useAllCustomCode(customCode);

  const { getEventsMaps, eventMapsLoading } = useEventMaps();
  useEffect(() => {
    setOptions(
      getEventsMaps()?.sort((a, b) =>
        a['label'].localeCompare(b['label'], undefined, { numeric: true }),
      ),
    );
  }, [getEventsMaps]);

  const getIconType = useCallback((severity) => {
    switch (severity) {
      case SeverityType.ALARM:
        return <AlarmIcon />;
      case SeverityType.WARNING:
        return <WarningIcon />;
      case SeverityType.INFORMATION:
        return <InformationIcon />;
      case SeverityType.CRITICAL:
        return (
          <SeverityIcon viewBox="0 0 16 12">
            <path d={Icons.SAFETY_POLYGON} />
            <path d={Icons.SAFETY_EXCLAMATORY} />
            <path d={Icons.SAFETY_CIRCLE} />
          </SeverityIcon>
        );
      default:
        return '';
    }
  }, []);

  useEffect(() => {
    const paramValues = params.get(DistributionLists.MODE)?.split('__');
    if (
      paramValues &&
      paramValues[0] !== DistributionLists.NEW &&
      paramValues[1] &&
      paramValues[2] &&
      eventManagementLists.length
    ) {
      if (isViewBy(viewBy, ViewBy.EVENT_MAP)) {
        const mapRecord = options?.find((value) => value.value == paramValues[1]);
        const eventRecord = eventManagementLists?.find((value) => value.eventId == paramValues[2]);
        eventRecord && viewDetail({ ...eventRecord, eventMapId: mapRecord?.value });
      } else {
        const eventRecord = eventManagementLists?.find((value) => value.eventId == paramValues[2]);
        eventRecord && viewDetail({ ...eventRecord, eventMapId: paramValues[1] });
      }
    }
    // Dont add eventManagementLists due to call stack issue.
  }, [params, viewDetail, options, isFetching]);

  useEffect(() => {
    if (eventCodeList?.length > 0) {
      setVisibleEventCodes(eventCodeList?.slice(0, pageSize));
    }
  }, [eventCodeList]);

  useEffect(() => {
    if (customCodeList?.length > 0) {
      setVisibleCustomCode(customCodeList?.slice(0, pageSize));
    }
  }, [customCodeList]);

  useEffect(() => {
    searchWorker.current.onmessage = (event) => {
      const filteredEventCodes = event.data;
      if (!filteredEventCodes?.length) return;
      const newSearchCode = filteredEventCodes[0];
      setVisibleEventCodes((prev) => {
        const maxVisibleCode = Math.max(...prev);
        const isSearchOutOfRange = newSearchCode > maxVisibleCode;

        let updatedList = [...prev];

        if (injectedSearchesRef.current.size > 0) {
          updatedList = updatedList.filter((code) => !injectedSearchesRef.current.has(code));
          injectedSearchesRef.current.clear();
        }

        if (isSearchOutOfRange) {
          updatedList.unshift(newSearchCode);
          injectedSearchesRef.current.add(newSearchCode);
        }
        return updatedList;
      });
    };
  }, []);

  useEffect(() => {
    if (isViewBy(viewBy, ViewBy.EVENT)) {
      setEventCode(true);
      setCustomCode(false);
      if (visibleEventCodes?.length) {
        setSecondaryOptions(visibleEventCodes?.map((code) => ({ label: code, value: code })));
      }
    } else if (isViewBy(viewBy, ViewBy.Custom)) {
      setCustomCode(true);
      setEventCode(false);

      setSecondaryOptions(
        visibleCustomCode?.length
          ? visibleCustomCode.map((code) => ({ label: code, value: code }))
          : [],
      );
    } else {
      setSecondaryOptions(options);
      setEventCode(false);
      setCustomCode(false);
      setVisibleEventCodes([]);
    }
  }, [
    viewBy?.value,
    options?.value,
    eventCodeList,
    visibleEventCodes,
    customCodeList,
    visibleCustomCode,
  ]);

  useEffect(() => {
    if (isViewBy(viewBy, ViewBy.Custom) && visibleCustomCode.length) {
      setSecondaryOptions(visibleCustomCode.map((code) => ({ label: code, value: code })));
    }
  }, [visibleCustomCode]);

  const loadMoreEventsCodes = () => {
    const nextIndex = visibleEventCodes.length;
    if (nextIndex < eventCodeList?.length) {
      const nextItems = eventCodeList?.slice(nextIndex, nextIndex + pageSize);
      setVisibleEventCodes((prev) => {
        const updatedEventCodes = new Set([...prev, ...nextItems]);
        return Array.from(updatedEventCodes);
      });
    }
  };

  const sortedDirection = useCallback(
    (metric) => (metric === sortMetric ? sortDirection : ''),
    [sortMetric, sortDirection],
  );

  const onEventChange = (e) => {
    setViewBy(e);
    resetFilter();
  };

  const handleFilterChange = useCallback(
    (_, key, value) => setCurrentSearch({ key, value }),
    [setCurrentSearch],
  );

  const handleFilterApply = useCallback(
    (_, columnKey, value) => {
      // Clear button case
      if (value === undefined || value === null || value.length === 0) {
        setFilterDefs(columnKey, null);
        return;
      }

      // Apply button case
      const normalizedValue = Array.isArray(value) ? value : [value];
      setFilterDefs(columnKey, normalizedValue);
    },
    [setFilterDefs],
  );


  const onEventMapChange = (e) => {
    setSelectedMapId(e);

    if (visibleEventCodes && visibleEventCodes.length > 0) {
      if (e.value < visibleEventCodes.length) {
        setVisibleEventCodes((prev) => {
          return prev.filter((item) => item <= prev.length);
        });
      }
    }
    resetFilter();
  };

  const isValidNumber = (value) => {
    return !isNaN(value) && value.trim() !== '';
  };

  const handleSearch = debounce((searchTerm) => {
    if (searchTerm && isValidNumber(searchTerm)) {
      searchWorker.current.postMessage({ eventCodeList, searchValue: searchTerm });
    }
  }, 50);

  const onInputChange = (newValue) => {
    handleSearch(newValue);
  };

  const customCellFn = useCallback(
    (columnKey, cellValue) => {
      const menuPopupCellFn = (values, emptyText) => {
        let cellElement = `${emptyText || ''}`;

        if (Array.isArray(cellValue) && cellValue.length > 0) {
          const text = cellValue.join(', ');
          const ellipsisTextLength = 15;

          let badgeText = '';
          if (cellValue.length > 1 && text.length > ellipsisTextLength + 1) {
            const preview = text.slice(0, ellipsisTextLength);
            const remainingCount = cellValue.length - preview.split(', ').length;

            if (remainingCount > 0) {
              badgeText = `+${remainingCount}`;
            }
          }
          cellElement =
            !badgeText || cellValue.length === 1 ? (
              <TooltipCell tooltip={text}>{text}</TooltipCell>
            ) : (
              <div>
                <EllipsisDivWrapper>{text}</EllipsisDivWrapper>
                <PopupMenu {...values} data={cellValue}>
                  <CellWrapBadge color={globalColors.stone1} label={badgeText} small />
                </PopupMenu>
              </div>
            );
        }

        return cellElement;
      };

      switch (columnKey) {
        case EventManagementColumns.EVENT_CODE:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.EVENT_MAP:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.EVENT_SEVERITY:
          return cellValue ? (
            <TooltipCell tooltip={cellValue}>{getIconType(cellValue)}</TooltipCell>
          ) : (
            ''
          );
        case EventManagementColumns.EVENT_NAME:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.PARENT_EVENTS: {
          const parentEventsIds = cellValue ? cellValue.join(', ') : '';
          return <TooltipCell tooltip={parentEventsIds}>{parentEventsIds}</TooltipCell>;
        }
        case EventManagementColumns.CHILD_EVENTS: {
          const childEventIds = cellValue ? cellValue.join(', ') : '';
          return <TooltipCell tooltip={childEventIds}>{childEventIds}</TooltipCell>;
        }
        case EventManagementColumns.INCLUDED_SITES:
          return menuPopupCellFn({ title: 'INCLUDED_SITES', headers: ['Name'] });
        case EventManagementColumns.EXCLUDED_SITES:
          return menuPopupCellFn({ title: 'EXCLUDED_SITES', headers: ['Name'] });
        case EventManagementColumns.MAX_REST_LIMIT:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.PRESENTATION_DELAY:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.CASE_CREATION_DELAY:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.FLAGGED_STATES:
          return cellValue ? (
            <div style={{ display: 'flex' }}>
              {cellValue.map((item, index) => (
                <FlaggedStateIcon
                  key={index}
                  style={{ backgroundColor: FLAGGED_STATES[item].color }}
                >
                  <TooltipCell tooltip={item}>{FLAGGED_STATES[item].letter}</TooltipCell>
                </FlaggedStateIcon>
              ))}
            </div>
          ) : (
            ''
          );
        case EventManagementColumns.BLOCK_AUTOMATION:
          return cellValue ? <CheckMark /> : '';
        case EventManagementColumns.BLOCK_CASE_ESCALATION:
          return cellValue ? <CheckMark /> : '';
        case EventManagementColumns.BLOCK_ALL_COMMANDS:
          return cellValue ? <CheckMark /> : '';
        case EventManagementColumns.DESCRIPTION:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.EVENT_NOTES:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.PROCEDURE_HANDLING_NAME:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        case EventManagementColumns.AUTOMATION_SEQUENCE_NAME:
          return cellValue ? <TooltipCell tooltip={cellValue}>{cellValue}</TooltipCell> : '';
        default:
          return null;
      }
    },
    [getIconType, openEMSidePanel, selectedMapId?.value, viewDetail],
  );

  const cellValueMapFn = useCallback((row) => {
    const {
      name,
      flaggedStates,
      maxResetLimit,
      presentationDelay,
      caseCreationDelay,
      eventRemarks,
      note,
      commandProperties,
      automationProperties,
      excludedsites,
      sequenceName,
      includedsites,
      procProperties,
      eventClass,
      eventMapName,
      sourceEventId,
      parentEventIds,
      childEventIds,
    } = row;
    return {
      //  [EventManagementColumns.EVENT_CODE]: +sourceEventId,
      [EventManagementColumns.EVENT_CODE]: isNaN(Number(sourceEventId))
        ? sourceEventId
        : Number(sourceEventId),

      [EventManagementColumns.EVENT_MAP]: eventMapName,
      [EventManagementColumns.EVENT_SEVERITY]: eventClass,
      [EventManagementColumns.EVENT_NAME]: name,
      [EventManagementColumns.PARENT_EVENTS]: parentEventIds,
      [EventManagementColumns.CHILD_EVENTS]: childEventIds,
      [EventManagementColumns.INCLUDED_SITES]: includedsites?.value,
      [EventManagementColumns.EXCLUDED_SITES]: excludedsites?.value,
      [EventManagementColumns.MAX_REST_LIMIT]: maxResetLimit || '0',
      [EventManagementColumns.PRESENTATION_DELAY]: presentationDelay || '0',
      [EventManagementColumns.CASE_CREATION_DELAY]: caseCreationDelay || '0',
      [EventManagementColumns.FLAGGED_STATES]: flaggedStates?.value,
      [EventManagementColumns.BLOCK_AUTOMATION]: automationProperties?.blockAutomation,
      [EventManagementColumns.BLOCK_CASE_ESCALATION]: automationProperties?.blockCaseEscalation,
      [EventManagementColumns.BLOCK_ALL_COMMANDS]: commandProperties?.blockCommands,
      [EventManagementColumns.DESCRIPTION]: eventRemarks,
      [EventManagementColumns.EVENT_NOTES]: note,
      [EventManagementColumns.PROCEDURE_HANDLING_NAME]: procProperties?.procTitle,
      [EventManagementColumns.AUTOMATION_SEQUENCE_NAME]: sequenceName,
    };
  }, []);

  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: EventManagementColumnsDefs,
    sortAction,
    sortedDirection,
    cellValueMapFn,
    customCellFn,
    onFilterApply: handleFilterApply,
    onFilterChange: handleFilterChange,
    filters: filterDefs,
    filterValues,
    draggable: true,
  });

  const { savePrefs, featPrefs } = useFeaturePrefs(AppScopes.ADMIN_CONFIGURE_EVENT_MANAGEMENT);

  const { onVisibilityChange: handleColumnChanges, onSelectionVisibilityChange } =
    useManageColumnVisibility({
      subKey: AdminDefs.EVENT_MANAGEMENT_SORT_STATE_ID,
      featPrefs,
      savePrefs,
      setVisibleColumns,
      updateColumnVisibility,
    });

  const emptyText = useCallback(
  (type) => {
    const isCustomView = viewBy?.value === ViewBy.Custom;
    const hasSelection = isCustomView ? selectedCustomEventId : selectedMapId;

    if (type === 'title') {
      return !hasSelection
        ? t('event_management_empty.no_data_selected', 'No Data Selected')
        : eventManagementLists.length === 0
          ? t('event_management_empty.no_data_found', 'No Data Found')
          : '';
    }
    return !hasSelection
      ? t(
          'event_management_empty.select_prompt',
          'Select an Event Map, Custom Case or Event Code to see existing configurations',
        )
      : eventManagementLists.length === 0
        ? t(
            'event_management_empty.no_configurations',
            'Selected Event Map, Custom Case or Event Code has no existing configurations',
          )
        : '';
  },
  [selectedMapId, selectedCustomEventId, viewBy?.value, eventManagementLists, t],
);

  if (!ready || eventMapsLoading || viewBy?.value !== previousViewByValueRef.current) {
    return <Loader />;
  }

  const eventLable =
    viewBy?.value === previousViewByValueRef.current
      ? isViewBy(viewBy, ViewBy.EVENT_MAP)
        ? 'Select Event Map'
        : isViewBy(viewBy, ViewBy.Custom)
          ? 'Select Custom'
          : 'Select Event Code'
      : null;

  const handleSecondaryChange = (option) => {
    if (viewBy?.value === ViewBy.Custom) {
      // ✅ Custom view → store eventId (S100, S111)
      setSelectedCustomEventId(option?.value);
    } else {
      // ✅ Event / Event Map view → existing behavior
      onEventMapChange(option);
    }
  };

  return (
    <PageContainer i18nReady={ready}>
      <HeaderWrap>
        <PageHeader title={t('event_management', 'Event Management')} />
      </HeaderWrap>
      <SubNavigation>
        <SelectContainer>
          <SelectWrapper>
            <Select
              maxWidth={270}
              minWidth={270}
              primary
              name="eventMap"
              label={t('events.code', 'VIEW BY')}
              options={primarayOptions}
              value={viewBy}
              onChange={onEventChange}
            />
          </SelectWrapper>
          <SelectWrapper>
            <Select
              minWidth={270}
              primary
              name="secondarySelect"
              label={t('events.code', '')}
              options={secondaryOptions}
              placeholder={t('events.code', eventLable)}
              value={
                viewBy?.value === ViewBy.Custom
                  ? secondaryOptions?.find((o) => o.value === selectedCustomEventId) || null
                  : selectedMapId
              }
              onChange={handleSecondaryChange}
              searchable={true}
              onScroll={loadMoreEventsCodes}
              onInputChange={onInputChange}
              scrollbarStyles={true}
            />
          </SelectWrapper>
        </SelectContainer>

        {viewBy?.value !== ViewBy.Custom && (
          <AuthRender
            capability={Capability.ALARM_MANAGEMENT}
            admin
            description="create new Event"
            siteLevel={false}
          >
            <AddEMWrapper onClick={(e) => openEMSidePanel(e, EventManagement.NEW)}>
              {t('new_event', '+ ADD EVENT')}
            </AddEMWrapper>
          </AuthRender>
        )}

        <ColumnSelectMenu
          translateFn={t}
          columnDef={EventManagementColumnsDefs}
          columnState={columns}
          onMenuClose={handleColumnChanges}
          onClumnSelection={onSelectionVisibilityChange}
          isMenuBottom={true}
          heightOffset={220}
          hiddenColumns={viewBy?.value === ViewBy.Custom ? ['parentEventIds', 'childEventIds'] : []}
        />
      </SubNavigation>
      {!ready || isLoading || viewBy?.value !== previousViewByValueRef.current ? (
        <Loader />
      ) : (
        <StyledDraggableTable
          scrollable={true}
          columns={columns}
          noData={!eventManagementLists.length}
          noDataDescription={emptyText()}
          noDataTitle={emptyText('title')}
          onRetry={null}
          values={eventManagementLists}
          columnGroupFactory={columnGroupFactory}
          columnFactory={columnFactory}
          sortAction={sortAction}
          cellFactory={cellFactory}
          onValueSelect={(...arg) => onRowSelect(...arg, selectedMapId)}
          rowKeyProperty={['uuid']}
          rowsSelected={selectedRow}
          dropHandler={(columnValue) =>
            savePrefs(columnValue, AdminDefs.EVENT_MANAGEMENT_SORT_STATE_ID)
          }
        />
      )}
    </PageContainer>
  );
};

EventManagementTable.propTypes = {
  onDrop: PropTypes.func,
  resetFilter: PropTypes.any,
  sortAction: PropTypes.func,
  scrollable: PropTypes.bool,
  viewDetail: PropTypes.func,
  onRowSelect: PropTypes.func,
  className: PropTypes.string,
  sortMetric: PropTypes.string,
  filterDefs: PropTypes.object,
  setFilterDefs: PropTypes.func,
  filterValues: PropTypes.object,
  openEMSidePanel: PropTypes.func,
  sortDirection: PropTypes.string,
  selectedSiteId: PropTypes.string,
  selectedAssetId: PropTypes.string,
  setVisibleColumns: PropTypes.func,
  theme: PropTypes.instanceOf(Object),
  updateColumnVisibility: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  selectedRow: PropTypes.array,
};

EventManagementTable.defaultProps = {
  filterDefs: {},
  selectedSiteId: '',
  selectedAssetId: '',
  sortAction: () => null,
  sortMetric: '',
  sortDirection: '',
};

export default EventManagementTable;
